import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Button, Card, DateInput, Field, Input, Select, Textarea } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { AGENT_DEPARTMENTS, AGENT_DESIGNATIONS, titleCase } from '../../lib/agents.js';
import { parseDayMonthYear, todayKey, toDayMonthYear } from '../../lib/dates.js';
import AgentIdCard from './AgentIdCard.jsx';
import PhotoUpload from './PhotoUpload.jsx';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  designation: '',
  department: '',
  employeeId: '',
  joiningDate: todayKey(),
  photoUrl: '',
  status: 'pending',
};

/**
 * Create or edit an agent.
 *
 * A page rather than a dialog because the ID card previews alongside the form:
 * the card is the reason most of these fields exist, so seeing it fill in as
 * you type is the fastest way to catch a typo before it reaches a printer.
 *
 * One component for both modes so the two can never drift — an edit form that
 * validates differently from the create form is how bad records get in.
 */
export default function AgentFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [isWorking, setIsWorking] = useState(false);

  const existing = useQuery({
    queryKey: ['admin-agents', id],
    queryFn: () => api.agents.detail(id),
    enabled: isEdit,
    retry: false,
  });

  // Reserved up front purely so the admin can see the badge number before
  // committing. The server re-checks it on create and wins any race. Not
  // fetched when editing: that agent already has a number.
  const employeeId = useQuery({
    queryKey: ['admin-agents', 'next-employee-id'],
    queryFn: api.agents.nextEmployeeId,
    enabled: !isEdit,
    staleTime: Infinity,
    retry: false,
  });

  // Seeded during render rather than from an effect: this is React's
  // adjusting-state-on-new-props case, and an effect here would render twice
  // and risk clobbering what the admin has already typed.
  const loaded = existing.data;
  const reserved = employeeId.data?.employeeId;
  const seedKey = isEdit ? loaded?._id : reserved;

  const [seededFor, setSeededFor] = useState(null);

  if (seedKey && seededFor !== seedKey) {
    setSeededFor(seedKey);

    if (isEdit) {
      setForm({
        name: loaded.name || '',
        employeeId: loaded.employeeId || '',
        email: loaded.email || '',
        phone: loaded.phone || '',
        address: loaded.address || '',
        designation: loaded.designation || '',
        department: loaded.department || '',
        joiningDate: toDayMonthYear(loaded.joiningDate),
        status: loaded.status || 'pending',
        photoUrl: loaded.photoUrl || '',
      });
    } else {
      // Only fills the badge number; anything already typed survives.
      setForm((current) => ({ ...current, employeeId: reserved }));
    }
  }

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  // Mirrors the server's zod schema, so the page can say what is wrong before
  // spending a round trip on it.
  const joiningDate = parseDayMonthYear(form.joiningDate);

  // Email is optional, but a half-typed one should not be submitted.
  const hasEmail = form.email.trim().length > 0;
  const isEmailWellFormed = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  const isEmployeeIdWellFormed = /^SH-\d{2,}$/.test(form.employeeId.trim());

  const isValid =
    form.name.trim().length >= 2 &&
    isEmployeeIdWellFormed &&
    form.phone.trim().length >= 6 &&
    Boolean(form.designation) &&
    Boolean(form.department) &&
    Boolean(joiningDate) &&
    (!hasEmail || isEmailWellFormed);

  const save = async () => {
    setIsWorking(true);

    const body = {
      name: form.name.trim(),
      employeeId: form.employeeId.trim(),
      phone: form.phone.trim(),
      designation: form.designation,
      department: form.department,
      // Sent as ISO; the dd/mm/yyyy is a display concern.
      joiningDate: joiningDate.toISOString(),
      status: form.status,
    };

    try {
      if (isEdit) {
        // null clears a field the admin emptied; omitting it would leave the
        // old value in place, which is not what an emptied box means.
        const agent = await api.agents.update(id, {
          ...body,
          email: hasEmail ? form.email.trim().toLowerCase() : null,
          address: form.address.trim() || null,
          photoUrl: form.photoUrl || null,
        });

        toast.success(`${agent.name} has been updated.`);
      } else {
        const agent = await api.agents.create({
          ...body,
          // Optional on the server: send nothing rather than an empty string.
          ...(hasEmail ? { email: form.email.trim().toLowerCase() } : {}),
          ...(form.address.trim() ? { address: form.address.trim() } : {}),
          ...(form.photoUrl ? { photoUrl: form.photoUrl } : {}),
        });

        toast.success(`${agent.name} has been added to the roster — ${agent.employeeId}.`);
      }

      queryClient.invalidateQueries({ queryKey: ['admin-agents'] });
      navigate('/agents');
    } catch (error) {
      // A duplicate email comes back as a 409 with a usable sentence.
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Page
      title={isEdit ? 'Edit agent' : 'New agent'}
      subtitle="The ID card on the right updates as you type. Names and roles are stored in capitals."
      actions={
        <>
          <Button variant="ghost" onClick={() => navigate('/agents')}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} loading={isWorking} disabled={!isValid}>
            {isEdit ? 'Save changes' : 'Create agent'}
          </Button>
        </>
      }
    >
      <div className="agent-form">
        <Card title="Details">
          <div className="stack">
            <Field label="Photo" hint="Square crop, used on the ID card. Optional.">
              <PhotoUpload
                value={form.photoUrl}
                onChange={(photoUrl) => setForm((current) => ({ ...current, photoUrl }))}
              />
            </Field>

            <div className="field-row">
              <Field label="Full name" id="agent-name">
                <Input id="agent-name" value={form.name} onChange={set('name')} autoFocus />
              </Field>
              <Field
                label="Employee ID"
                id="agent-employee-id"
                hint={
                  isEdit
                    ? 'The printed card carries this number — change it only deliberately.'
                    : 'Allocated automatically. Override it if you need to.'
                }
                error={
                  form.employeeId && !isEmployeeIdWellFormed
                    ? 'Use the form SH-01.'
                    : null
                }
              >
                <Input
                  id="agent-employee-id"
                  value={form.employeeId}
                  onChange={set('employeeId')}
                  placeholder={employeeId.isLoading ? 'Allocating…' : 'SH-01'}
                  error={Boolean(form.employeeId) && !isEmployeeIdWellFormed}
                />
              </Field>
            </div>

            <div className="field-row">
              <Field
                label="Email"
                id="agent-email"
                hint="Optional. Must be unique across the roster."
                error={hasEmail && !isEmailWellFormed ? 'That does not look like an email address.' : null}
              >
                <Input
                  id="agent-email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  error={hasEmail && !isEmailWellFormed}
                />
              </Field>
              <Field label="Phone" id="agent-phone" hint="Required.">
                <Input id="agent-phone" type="tel" value={form.phone} onChange={set('phone')} />
              </Field>
            </div>

            <div className="field-row">
              <Field label="Designation" id="agent-designation">
                <Select id="agent-designation" value={form.designation} onChange={set('designation')}>
                  <option value="">Select a designation</option>
                  {AGENT_DESIGNATIONS.map((value) => (
                    <option key={value} value={value}>
                      {titleCase(value)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Department" id="agent-department">
                <Select id="agent-department" value={form.department} onChange={set('department')}>
                  <option value="">Select a department</option>
                  {AGENT_DEPARTMENTS.map((value) => (
                    <option key={value} value={value}>
                      {titleCase(value)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Address" id="agent-address" hint="Optional.">
              <Textarea id="agent-address" value={form.address} onChange={set('address')} rows={2} />
            </Field>

            <div className="field-row">
              <Field
                label="Joining date"
                id="agent-joining"
                hint="dd/mm/yyyy — type it, or use the calendar."
                error={form.joiningDate && !joiningDate ? 'Enter a real date as dd/mm/yyyy.' : null}
              >
                <DateInput
                  id="agent-joining"
                  value={form.joiningDate}
                  onChange={(joiningDateValue) =>
                    setForm((current) => ({ ...current, joiningDate: joiningDateValue }))
                  }
                  error={Boolean(form.joiningDate) && !joiningDate}
                />
              </Field>
              <Field
                label="Status"
                id="agent-status"
                hint="Pending is the safe default until the record has been checked."
              >
                <Select id="agent-status" value={form.status} onChange={set('status')}>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </Field>
            </div>
          </div>
        </Card>

        <div className="agent-form__preview">
          {/*
            The card is given parsed values, not the raw form: `joiningDate`
            is a dd/mm/yyyy string here, which `new Date` would read as US
            month-first and render as the wrong day.
          */}
          <AgentIdCard
            agent={{
              ...form,
              joiningDate: joiningDate ? joiningDate.toISOString() : null,
              employeeId: form.employeeId,
            }}
          />
        </div>
      </div>
    </Page>
  );
}
